export const POSTGREST_PAGE_SIZE = 1000;

type Page<Row> = {
  data: Row[] | null;
  error: { message: string } | null;
  count?: number | null;
};

type FetchPage<Row> = (options: { from: number; to: number; withCount: boolean }) => PromiseLike<Page<Row>>;

/**
 * Reads a whole table through PostgREST, which caps every response at 1000 rows — a plain `select()`
 * on a table above that silently returns a truncated slice instead of erroring.
 *
 * The first page carries `count: "exact"` so the remaining ones can go out in parallel; paging
 * sequentially over tens of thousands of rows would mean a dozen chained round-trips.
 *
 * The caller's query MUST order by a unique column. Ordering by a non-unique one (a name, a
 * timestamp) lets rows shift between pages, which both drops and duplicates records.
 */
export const fetchAllPostgrestRows = async <Row>(fetchPage: FetchPage<Row>): Promise<Row[]> => {
  const firstPage = await fetchPage({ from: 0, to: POSTGREST_PAGE_SIZE - 1, withCount: true });

  if (firstPage.error) {
    throw new Error(firstPage.error.message);
  }

  const rows = firstPage.data ?? [];
  const total = firstPage.count ?? rows.length;

  if (total <= rows.length) {
    return rows;
  }

  const remainingPages = Math.ceil((total - rows.length) / POSTGREST_PAGE_SIZE);
  const pages = await Promise.all(
    Array.from({ length: remainingPages }, (_, index) => {
      const from = (index + 1) * POSTGREST_PAGE_SIZE;
      return fetchPage({ from, to: from + POSTGREST_PAGE_SIZE - 1, withCount: false });
    }),
  );

  const failed = pages.find((page) => page.error);

  if (failed?.error) {
    throw new Error(failed.error.message);
  }

  return pages.reduce<Row[]>((all, page) => all.concat(page.data ?? []), rows);
};
